import { useState, useRef, useEffect } from 'react';

function LazyCoverImage({ 
    src, 
    alt, 
    className = 'cover-image',
    fallback = null,
    placeholder = null,
    aspectRatio = '16:9',
    isAvatar = false,
    avatarWidth = '30%'
}) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const [hasError, setHasError] = useState(false);
    const imgRef = useRef(null);
    const observerRef = useRef(null);

    // Default fallback cover image - rectangular pattern
    const getDefaultCover = () => {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjZjBmMGYwIi8+CjxwYXRoIGQ9Ik0xMjAgNjBMMTQwIDkwSDE2MEwxODAgNjBMMjAwIDkwSDIyMEwyNDAgNjBMMjYwIDkwSDI4MEwzMDAgNjBWMTIwSDIwVjYwSDEyMFoiIGZpbGw9IiNkZGQiLz4KPGNpcmNsZSBjeD0iODAiIGN5PSI4MCIgcj0iMjAiIGZpbGw9IiNjY2MiLz4KPC9zdmc+';
    };

    // Default fallback for avatars - circular user icon
    const getDefaultAvatar = () => {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDE1MCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxjaXJjbGUgY3g9Ijc1IiBjeT0iNzUiIHI9Ijc1IiBmaWxsPSIjZTBlMGUwIi8+CjxjaXJjbGUgY3g9Ijc1IiBjeT0iNTUiIHI9IjI1IiBmaWxsPSIjYmRiZGJkIi8+CjxwYXRoIGQ9Ik0zNSAxMzBDMzUgMTA1IDUyIDg1IDc1IDg1Qzk4IDg1IDExNSAxMDUgMTE1IDEzMCIgZmlsbD0iI2JkYmRiZCIvPgo8L3N2Zz4=';
    };

    // Calculate container dimensions based on aspect ratio
    const getContainerStyle = () => {
        const [width, height] = aspectRatio.split(':').map(Number);
        const paddingBottom = `${(height / width) * 100}%`;
        
        return {
            position: 'relative',
            width: '100%',
            paddingBottom: paddingBottom,
            backgroundColor: '#f0f0f0',
            background: '#f0f0f0',
            borderRadius: 'var(--border-radius, 8px)',
            overflow: 'hidden'
        };
    };

    // Get image styles - different for avatars vs cover images
    const getImageStyle = () => {
        const baseStyle = {
            position: 'absolute',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out'
        };

        if (isAvatar) {
            // Avatar: centered, circular - equal width/height within container
            return {
                ...baseStyle,
                width: avatarWidth,
                height: '90%',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                objectFit: 'cover',
                borderRadius: '50%'
            };
        }

        // Cover image: fill the container
        return {
            ...baseStyle,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
        };
    };

    // Set up intersection observer for lazy loading
    useEffect(() => {
        const imgElement = imgRef.current;
        if (!imgElement) return;

        // If IntersectionObserver is not supported, load immediately
        if (!window.IntersectionObserver) {
            setIsInView(true);
            return;
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    setIsInView(true);
                    // Disconnect observer once image is in view
                    observerRef.current?.disconnect();
                }
            },
            {
                // Start loading when image is 100px away from viewport
                rootMargin: '100px',
                threshold: 0.1
            }
        );

        observerRef.current.observe(imgElement);

        // Cleanup
        return () => {
            observerRef.current?.disconnect();
        };
    }, []);

    const handleLoad = () => {
        setIsLoaded(true);
    };

    const handleError = () => {
        setHasError(true);
        setIsLoaded(true); // Consider loaded even with error to show fallback
    };

    // Determine which image source to use
    const imageSrc = hasError 
        ? (fallback || (isAvatar ? getDefaultAvatar() : getDefaultCover())) 
        : src;

    return (
        <div 
            ref={imgRef}
            className={`lazy-cover-container ${className}-container ${isAvatar ? 'is-avatar-container' : ''}`}
            style={getContainerStyle()}
        >
            {/* Placeholder while loading */}
            {!isLoaded && placeholder && (
                <div 
                    className="cover-placeholder"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {placeholder}
                </div>
            )}
            
            {/* Actual image - only load when in view */}
            {isInView && (
                <img
                    src={imageSrc}
                    alt={alt}
                    className={`${className} ${isLoaded ? 'loaded' : 'loading'} ${isAvatar ? 'avatar-image' : ''}`}
                    onLoad={handleLoad}
                    onError={handleError}
                    style={getImageStyle()}
                />
            )}
            
            {/* Loading spinner placeholder */}
            {!isLoaded && !placeholder && (
                <div 
                    className="cover-loading"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: '2px solid #ddd',
                        borderTop: '2px solid #666',
                        animation: 'spin 1s linear infinite'
                    }}
                />
            )}
        </div>
    );
}

export default LazyCoverImage;
